import { useEffect, useMemo, useRef, useState } from 'react';

import {
  type MachineListRow,
  type TicketDetail,
  type TicketOverrideAutoSlotLabels,
  type TicketOverrideSlot,
  type UiCatalogueModel,
  type VerificationProfileSummary,
} from '@nightshift/ui-types';

import { Spinner } from '../../components/primitives.tsx';
import { RouteListEditor, type RouteMachine } from '../../components/route-list-editor.tsx';
import { registerUnsavedEditor } from '../../lib/unsaved.ts';
import { api } from '../../live/api.ts';
import { useStore } from '../../live/context.tsx';
import { isTicketDraftConflict, reconcileTicketDraft, ticketDraftSnapshot } from '../../lib/ticket-draft.ts';
import { DraftConflictWarning } from './draft-conflict.tsx';
import {
  catalogueWithSwitches,
  cloneSlot,
  duplicateScopes,
  EXECUTION_STATE_OPTIONS,
  firstUnusedScope,
  overridesFromRows,
  rowsFromOverrides,
  rowsFromSlot,
  scopeKey,
  slotFromRows,
  slotIsAuto,
  stepOptions,
  type ExecutionState,
  type OverrideEditorRow,
} from '../../lib/execution-options.ts';
import { routePhrase } from '../../lib/route-list.ts';

const AUTO_VALUE = '';

function autoLabel(applied: string): string {
  return `Auto (Project Default) - ${applied}`;
}

/**
 * What a state row inherits when left on Auto: the ticket-wide rows if there are any,
 * otherwise that state's own project/system default (routes are per role, so the
 * Implementing default is not the Refining one).
 */
function inheritedPhrase(ticket: TicketOverrideSlot, auto: TicketOverrideAutoSlotLabels): string {
  if (!slotIsAuto(ticket)) {
    const rows = rowsFromSlot(ticket);
    if (rows.length > 0) return `ticket-wide: ${rows.map(routePhrase).join(' → ')}`;
    return `ticket-wide: ${[ticket.machine, ticket.harness, ticket.models?.join(', ')].filter(Boolean).join(' · ')}`;
  }
  return `${auto.machine} · ${auto.harness} · ${auto.models}`;
}

/**
 * Ticket-wide defaults plus an addable list of state overrides. A state row can
 * optionally narrow itself to one execution step without exposing a second,
 * competing "state/activity" editor. Every slot is the same four-column route list the
 * rest of the product uses; an empty list is Auto.
 */
export function OverridesTab({
  detail,
  onReloaded,
}: {
  detail: TicketDetail;
  onReloaded: () => Promise<void>;
}) {
  const store = useStore();
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [ticketSlot, setTicketSlot] = useState(() => cloneSlot(detail.overrides.ticket));
  const [verificationProfile, setVerificationProfile] = useState(
    detail.overrides.verificationProfile ?? '',
  );
  const [verificationScenarios, setVerificationScenarios] = useState(
    (detail.overrides.verificationScenarios ?? []).join('\n'),
  );
  const [sizeTier, setSizeTier] = useState<string>(detail.overrides.sizeTier ?? '');
  const [profiles, setProfiles] = useState<VerificationProfileSummary[]>([]);
  const [rows, setRows] = useState<OverrideEditorRow[]>(() => rowsFromOverrides(detail.overrides));
  const [machines, setMachines] = useState<MachineListRow[]>([]);
  const [machinesStatus, setMachinesStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [catalogue, setCatalogue] = useState<UiCatalogueModel[]>([]);
  const rowSequence = useRef(0);
  const ticketKeyRef = useRef(detail.ticket.ticketKey);
  const draftVersionRef = useRef(detail.ticket.version);
  const draftOverridesRef = useRef(detail.overrides);
  const dirtyRef = useRef(false);
  const incomingOverridesSnapshot = ticketDraftSnapshot(detail.overrides);
  const duplicates = useMemo(() => duplicateScopes(rows), [rows]);
  const nextScope = useMemo(() => firstUnusedScope(rows), [rows]);
  const routeMachines: RouteMachine[] = useMemo(
    () => machines.map((machine) => ({ name: machine.name, installedHarnesses: machine.installedHarnesses, live: machine.live, paused: machine.paused })),
    [machines],
  );
  const markDirty = () => {
    if (!dirtyRef.current) {
      draftVersionRef.current = detail.ticket.version;
      draftOverridesRef.current = detail.overrides;
    }
    dirtyRef.current = true;
  };

  useEffect(() => {
    const refresh = reconcileTicketDraft({
      dirty: dirtyRef.current,
      draftTicketKey: ticketKeyRef.current,
      draftVersion: draftVersionRef.current,
      incomingTicketKey: detail.ticket.ticketKey,
      incomingVersion: detail.ticket.version,
      draftSnapshot: ticketDraftSnapshot(draftOverridesRef.current),
      incomingSnapshot: incomingOverridesSnapshot,
    });
    ticketKeyRef.current = detail.ticket.ticketKey;
    if (refresh !== 'reset') {
      if (refresh === 'conflict') setConflict(true);
      return;
    }
    dirtyRef.current = false;
    draftVersionRef.current = detail.ticket.version;
    draftOverridesRef.current = detail.overrides;
    setConflict(false);
    setTicketSlot(cloneSlot(detail.overrides.ticket));
    setVerificationProfile(detail.overrides.verificationProfile ?? '');
    setVerificationScenarios((detail.overrides.verificationScenarios ?? []).join('\n'));
    setSizeTier(detail.overrides.sizeTier ?? '');
    setRows(rowsFromOverrides(detail.overrides));
    // The snapshot dependency spots same-version remote edits; the dirty guard above keeps
    // those refreshes from replacing the local form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.ticket.ticketKey, detail.ticket.version, incomingOverridesSnapshot]);

  useEffect(
    () => registerUnsavedEditor({ isDirty: () => dirtyRef.current, what: `the ${detail.ticket.ticketKey} overrides` }),
    [detail.ticket.ticketKey],
  );

  useEffect(() => {
    let active = true;
    void api.machines().then(
      (page) => {
        if (!active) return;
        setMachines(page.items);
        setMachinesStatus('ready');
      },
      () => {
        if (!active) return;
        setMachinesStatus('error');
      },
    );
    void api.verificationProfiles().then(
      (page) => {
        if (!active) return;
        setProfiles(page.items);
      },
      () => {
        /* keep the fallback names in the select */
      },
    );
    // The live catalogue carries runtime-discovered families and each model's own thinking
    // levels; the Settings switches say which of them this project may use. A failed
    // switches read degrades to the unfiltered catalogue rather than an empty one.
    void (async () => {
      const [live, config] = await Promise.all([
        api.modelsCatalogue(detail.ticket.projectSlug).catch(() => null),
        api.config(detail.ticket.projectSlug).catch(() => null),
      ]);
      if (!active || !live) return;
      setCatalogue(catalogueWithSwitches(live.models, config?.vendors ?? []));
    })();
    return () => {
      active = false;
    };
  }, [detail.ticket.projectSlug]);

  async function save() {
    if (conflict) return;
    if (duplicates.size > 0) {
      store.toast('bad', 'Each state or step can only have one override');
      return;
    }
    const scenarios = verificationScenarios
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    const overrides = {
      ...overridesFromRows(ticketSlot, verificationProfile.trim() || null, rows, detail.overrides),
      verificationScenarios: scenarios.length > 0 ? scenarios : null,
      sizeTier: sizeTier === 'small' || sizeTier === 'standard' ? sizeTier : null,
    };
    setBusy(true);
    try {
      await api.setOverrides(
        detail.ticket.ticketKey,
        overrides,
        draftVersionRef.current,
        draftOverridesRef.current,
      );
      store.toast('good', 'Overrides saved');
      dirtyRef.current = false;
      setConflict(false);
      await onReloaded();
    } catch (error) {
      if (isTicketDraftConflict(error)) setConflict(true);
      store.toast('bad', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  function updateRow(id: string, patch: Partial<Omit<OverrideEditorRow, 'id'>>) {
    markDirty();
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  }

  function addRow() {
    if (!nextScope) return;
    markDirty();
    rowSequence.current += 1;
    setRows((current) => [
      ...current,
      {
        id: `new-${rowSequence.current}`,
        state: nextScope.state,
        step: nextScope.step,
        slot: cloneSlot(),
      },
    ]);
  }

  const machinesNote =
    machinesStatus === 'loading'
      ? 'Loading registered machines…'
      : machinesStatus === 'error'
        ? 'Machine list unavailable. Saved choices are preserved.'
        : null;

  return (
    <div className="drawer-overrides">
      {conflict ? <DraftConflictWarning field="overrides" /> : null}
      <p className="drawer-overrides-intro">
        Leave a list empty to inherit the project and Nightshift routes. Rows are tried in
        order; give every row the same machine to pin the work there. Ticket-wide rows apply
        to every agent run; state overrides are only for exceptions.
      </p>
      {detail.overridesError ? (
        <p className="drawer-overrides-error" role="status">
          Stored overrides could not be read ({detail.overridesError}). Saving writes a valid document.
        </p>
      ) : null}
      {machinesNote ? <p className="drawer-override-inherit">{machinesNote}</p> : null}

      <section className="drawer-override-section" aria-labelledby="verification-profile-heading">
        <header className="drawer-override-heading">
          <div>
            <h3 id="verification-profile-heading">Ticket type</h3>
            <p>A named type wins over the refiner. Auto lets Refine pick, then standard-code.</p>
          </div>
        </header>
        <label className="drawer-overrides-profile">
          <span>Ticket type</span>
          <select
            className="input"
            value={verificationProfile}
            onChange={(event) => {
              markDirty();
              setVerificationProfile(event.target.value);
            }}
          >
            <option value={AUTO_VALUE}>{autoLabel(detail.overrideAuto.verificationProfile)}</option>
            {(profiles.length > 0
              ? profiles
              : [
                  { name: 'standard-code' },
                  { name: 'visual-change' },
                  { name: 'gameplay-change' },
                  { name: 'performance-change' },
                ]
            ).map((entry) => (
              <option key={entry.name} value={entry.name}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>
        <label className="drawer-overrides-profile">
          <span>Ticket size</span>
          <select
            className="input"
            value={sizeTier}
            onChange={(event) => {
              markDirty();
              setSizeTier(event.target.value);
            }}
          >
            <option value={AUTO_VALUE}>Auto - {detail.overrideAuto.sizeTier}</option>
            <option value="small">small</option>
            <option value="standard">standard</option>
          </select>
          <span className="dim">
            A small ticket tries each stage's light routes first and moves to the standard routes after a failure,
            bounce or stall. Nothing changes where no light route is configured.
          </span>
        </label>
        <label className="drawer-overrides-profile">
          <span>Required scenarios</span>
          <textarea
            className="input"
            rows={3}
            value={verificationScenarios}
            placeholder={'capture:level-3-complete@1280x720#keyboard\ncapture:main-menu'}
            onChange={(event) => {
              markDirty();
              setVerificationScenarios(event.target.value);
            }}
          />
          <span className="dim">
            One per line as check:scenario, with an optional @viewport and #input mode. Pinned into the
            next attempt's contract, so a capture that skipped a named scenario cannot pass.
          </span>
        </label>
      </section>

      <section className="drawer-override-section" aria-labelledby="ticket-wide-heading">
        <header className="drawer-override-heading">
          <div>
            <h3 id="ticket-wide-heading">Ticket-wide</h3>
            <p>Used for every agent-run state unless a row below replaces it.</p>
          </div>
        </header>
        <RouteListEditor
          rows={rowsFromSlot(ticketSlot)}
          onChange={(next) => {
            markDirty();
            setTicketSlot(slotFromRows(next));
          }}
          machines={routeMachines}
          models={catalogue}
          disabled={busy}
          label="Ticket-wide route"
          anyMachineLabel="Any machine"
          emptyText={
            <>
              Auto. Inherits{' '}
              <strong>
                {detail.overrideAuto.machine} · {detail.overrideAuto.harness} · {detail.overrideAuto.models}
              </strong>
              .
            </>
          }
        />
      </section>

      <section className="drawer-override-section" aria-labelledby="state-overrides-heading">
        <header className="drawer-override-heading">
          <div>
            <h3 id="state-overrides-heading">State overrides</h3>
            <p>Add only the states that need to run differently on this ticket.</p>
          </div>
          <button
            type="button"
            className="btn btn-sm drawer-override-add"
            disabled={busy || nextScope === null}
            onClick={addRow}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M8 3v10M3 8h10" />
            </svg>
            Add state override
          </button>
        </header>

        {rows.length === 0 ? (
          <p className="drawer-overrides-empty">No state-specific overrides. Ticket-wide settings apply.</p>
        ) : (
          <div className="drawer-override-rows">
            {rows.map((row, index) => {
              const rowScope = scopeKey(row.state, row.step);
              const rowSteps = stepOptions(row.state);
              const duplicate = duplicates.has(rowScope);
              return (
                <article className="drawer-override-row" key={row.id}>
                  <div className="drawer-override-row-head">
                    <span className="drawer-section-kicker">Override {index + 1}</span>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={busy}
                      onClick={() => {
                        markDirty();
                        setRows((current) => current.filter((candidate) => candidate.id !== row.id));
                      }}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="drawer-override-scope-fields">
                    <label>
                      <span>State</span>
                      <select
                        className="input"
                        value={row.state}
                        onChange={(event) =>
                          updateRow(row.id, {
                            state: event.target.value as ExecutionState,
                            step: null,
                          })
                        }
                      >
                        {EXECUTION_STATE_OPTIONS.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                    </label>
                    {rowSteps.length > 0 ? (
                      <label>
                        <span>Step</span>
                        <select
                          className="input"
                          value={row.step ?? AUTO_VALUE}
                          onChange={(event) =>
                            updateRow(row.id, { step: event.target.value || null })
                          }
                        >
                          <option value={AUTO_VALUE}>All {row.state.toLowerCase()} work</option>
                          {rowSteps.map((step) => (
                            <option key={step} value={step}>
                              {step} only
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <div className="drawer-override-all-work">
                        <span>Applies to</span>
                        <strong>All {row.state} work</strong>
                      </div>
                    )}
                  </div>
                  {duplicate ? (
                    <p className="drawer-overrides-error" role="alert">
                      {rowScope} already has an override. Choose another state or step.
                    </p>
                  ) : null}
                  <RouteListEditor
                    rows={rowsFromSlot(row.slot)}
                    onChange={(next) => updateRow(row.id, { slot: slotFromRows(next) })}
                    machines={routeMachines}
                    models={catalogue}
                    disabled={busy}
                    label={`${rowScope} route`}
                    anyMachineLabel="Any machine"
                    emptyText={
                      <>
                        Auto. Inherits{' '}
                        <strong>{inheritedPhrase(ticketSlot, detail.overrideAuto.states[row.state])}</strong>.
                      </>
                    }
                  />
                </article>
              );
            })}
          </div>
        )}
      </section>

      <div className="drawer-overrides-actions">
        <button
          type="button"
          className="btn btn-accent"
          disabled={busy || conflict || duplicates.size > 0}
          onClick={() => void save()}
        >
          {busy ? <Spinner /> : null} Save overrides
        </button>
      </div>
    </div>
  );
}
